/**
 * SparkStoreModelsService.js
 * Service for fetching and caching AI model configurations from Spark Store
 */
import ApiClient from "./ApiClient";

class SparkStoreModelsService {
    constructor() {
        this.cache = null;
        this.cacheExpiry = null;
        this.CACHE_DURATION = 60 * 60 * 1000; // 1 hour
    }

    /**
     * Fetches store chat configurations from the API
     * @returns {Promise<Object>} Model configurations
     */
    async getStoreConfigs() {
        // Return cached data if still valid
        if (this.cache && this.cacheExpiry && Date.now() < this.cacheExpiry) {
            console.log("[SparkStoreModels] Returning cached configurations");
            return this.cache;
        }

        try {
            console.log("[SparkStoreModels] Fetching configurations from API");
            const data = await ApiClient.get("/api/sparkstore/models");

            // Cache the response
            this.cache = data;
            this.cacheExpiry = Date.now() + this.CACHE_DURATION;

            console.log("[SparkStoreModels] Configurations fetched successfully", {
                modelGroups: data.model_groups?.length || 0,
                conversationStyles: data.conversation_style?.length || 0,
                responseLengths: data.response_length?.length || 0
            });

            return data;
        } catch (error) {
            console.error("[SparkStoreModels] Failed to fetch configurations:", error);
            throw error;
        }
    }

    /**
     * Transforms model_groups from API into the format expected by the UI
     * @param {Array} modelGroups - Array of model groups from API
     * @returns {Array} Formatted models with value, label, desc
     */
    transformModels(modelGroups) {
        if (!modelGroups || !Array.isArray(modelGroups)) return [];

        const models = [];

        modelGroups.forEach(group => {
            if (group.models && Array.isArray(group.models)) {
                group.models.forEach(model => {
                    models.push({
                        value: model.display_name || model.key,
                        label: model.display_name || model.key,
                        desc: model.description || "",
                        key: model.key,
                        groupKey: group.group_key,
                        supportsThinking: model.supports_thinking || false,
                        supportsReasoning: model.supports_reasoning || false,
                        allowCustomization: model.allow_customization !== false,
                        default: model.default || false
                    });
                });
            }
        });

        return models;
    }

    /**
     * Transforms conversation_style from API into UI format
     * @param {Array} conversationStyle - Array of conversation styles from API
     * @returns {Array} Formatted styles
     */
    transformStyles(conversationStyle) {
        if (!conversationStyle || !Array.isArray(conversationStyle)) return [];

        return conversationStyle.map(style => ({
            value: style.key || style.display_name,
            label: style.display_name || style.key,
            desc: `Temperature: ${style.temperature}`,
            temperature: parseFloat(style.temperature) || 0.5,
            default: style.default || false
        }));
    }

    /**
     * Transforms response_length from API into UI format
     * @param {Array} responseLength - Array of response lengths from API
     * @returns {Array} Formatted response lengths
     */
    transformResponseLengths(responseLength) {
        if (!responseLength || !Array.isArray(responseLength)) return [];

        return responseLength.map(length => ({
            value: length.key || length.display_name,
            label: length.display_name || length.key,
            desc: `Response style: ${length.value || length.key}`,
            default: length.default || false
        }));
    }

    /**
     * Clear the cache (useful for testing or force refresh)
     */
    clearCache() {
        this.cache = null;
        this.cacheExpiry = null;
    }
}

// Export singleton instance
const sparkStoreModelsService = new SparkStoreModelsService();
export default sparkStoreModelsService;